import { validate as validateUuid } from "uuid";
import Constants from "src/utils/constants";
import Helper from "src/utils/helper";
import {
  buildFieldMap,
  isLookupField
} from "src/utils/emailTemplate/responseVariables";

function isRawId(value) {
  return (
    typeof value === "number" ||
    (typeof value === "string" && validateUuid(value.trim()))
  );
}

// Mirrors how findDynamicKeyInExpression renders a token: a property missing
// from the object returns the whole object, which stringifies as
// [object Object].
function rendersUnreadably(value, property) {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) {
    return value.some((item) => item !== null && typeof item === "object");
  }
  if (typeof value !== "object") return false;
  if (!property || !(property in value)) return true;
  const target = value[property];
  return target !== null && typeof target === "object";
}

function withoutLoops(expression) {
  let result = expression.replace(
    /\{\{#each\s+\$\(([^)]+)\)(?:\.([^}]+))?\}\}([\s\S]*?)\{\{\/each\}\}/g,
    ""
  );

  const loopRegex = /\$\(([^)[\]]+)\)\.loop\(/g;
  let loopMatch;
  while ((loopMatch = loopRegex.exec(result)) !== null) {
    const startIndex = loopMatch.index + loopMatch[0].length;
    let parenCount = 1;
    let endIndex = startIndex;

    while (endIndex < result.length && parenCount > 0) {
      if (result[endIndex] === "(") {
        parenCount++;
      } else if (result[endIndex] === ")") {
        parenCount--;
      }
      endIndex++;
    }

    result =
      result.substring(0, loopMatch.index) +
      result.substring(parenCount === 0 ? endIndex : startIndex);
    loopRegex.lastIndex = loopMatch.index;
  }

  return result;
}

async function loadLookupKeys(source) {
  if (
    source?.type !== Constants.enums.EMAIL_SOURCE_TYPES.FORM ||
    !source.form_template_id
  ) {
    return new Set();
  }
  try {
    const { pagesById } = await Helper.loadFormPages(source.form_template_id);
    const fieldMap = buildFieldMap(pagesById);
    return new Set(
      Object.keys(fieldMap).filter((id) => isLookupField(fieldMap[id]))
    );
  } catch (error) {
    console.error("[deliveryGuard] could not load lookup fields", error);
    return new Set();
  }
}

/**
 * Returns a copy of the send-time data in which every value an email template's
 * subject or body would render unreadably is replaced with "ERROR", so the
 * unchanged `findDynamicKeyInExpression` then prints "ERROR" for that token.
 *
 * Unreadable means:
 *   - it would render `[object Object]` — an object (or array of objects)
 *     behind a bare token, or a `.property` that is missing or itself an
 *     object;
 *   - it is a raw id (uuid-shaped string or number) on a lookup field of the
 *     template's form — with or without a `.property`, since a string ignores
 *     the property and prints as-is.
 *
 * Blank values are left blank. Lookup fields are only known for `form`
 * sources; if the form cannot be loaded, only the `[object Object]` check runs.
 *
 * @param {Object} tpl email_templates row
 * @param {Object} data the data the send path resolves tokens against
 * @returns {Promise<Object>} guarded copy, or `data` itself when there is nothing to guard
 */
async function guardTemplateData(tpl, data) {
  const expressions = [tpl?.template?.subject, tpl?.template?.body].filter(
    (expression) => typeof expression === "string" && expression.includes("$(")
  );
  if (!expressions.length) return data;

  const lookupKeys = await loadLookupKeys(tpl.template.source);
  const guarded = Helper.deepCopy(data ?? {});

  for (const expression of expressions) {
    for (const [, key, property] of withoutLoops(expression).matchAll(
      Constants.constants.EMAIL_TOKEN_PATTERN
    )) {
      const value = Helper.findAndSetKeyInObject(guarded, key);
      const unreadable =
        (lookupKeys.has(key) && isRawId(value)) ||
        rendersUnreadably(value, property?.slice(1));
      if (unreadable) Helper.findAndSetKeyInObject(guarded, key, "ERROR");
    }
  }

  return guarded;
}

/**
 * The subject and body every template send path delivers: the template's own
 * subject/body resolved against the guarded data. A trigger's inline
 * `overrides.subject` / `overrides.body` win when set, and resolve against the
 * raw data — inline overrides are not guarded.
 *
 * @returns {Promise<{subject: string, body: string}>}
 */
export async function renderTemplateMessage(tpl, data, overrides = {}) {
  const guarded = await guardTemplateData(tpl, data);
  return {
    subject: Helper.isStringValueExist(overrides.subject)
      ? Helper.findDynamicKeyInExpression(overrides.subject, data)
      : Helper.findDynamicKeyInExpression(tpl?.template?.subject, guarded),
    body: Helper.isStringValueExist(overrides.body)
      ? Helper.findDynamicKeyInExpression(overrides.body, data)
      : Helper.findDynamicKeyInExpression(tpl?.template?.body, guarded)
  };
}
